import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, TrendingUp, Building2, Users } from "lucide-react";
import { api } from "../api";
import { useI18n } from "../i18n";
import SEO from "../components/SEO";
import { ArticleCard } from "./InsightsIndex";

// Ogni regione: dati SEO ottimizzati per long-tail.
const REGIONS = {
  lombardia: {
    name: "Lombardia",
    tagline: "42.500 PMI in transizione. Milano capitale del passaggio generazionale italiano.",
    ateco_top: "Meccanica di precisione, moda, food premium",
    provinces: ["Milano", "Brescia", "Bergamo", "Varese", "Como", "Monza"],
    share_pct: 22.0,
    avg_multiple: 5.1,
    pmi_count: 42500,
    intro: "La Lombardia concentra il 22% del deal flow del micro-mid market italiano. Milano è il primo polo M&A del Paese, ma la vera ricchezza è distribuita tra Brescia, Bergamo, Varese, Como e Monza — dove le PMI meccaniche, di moda e food premium cercano da anni una successione senza trovarla. GIANO acquisisce e mantiene queste aziende, senza mai rivenderle.",
    faq: [
      { q: "Come vendere un'azienda in Lombardia senza broker?", a: "Con GIANO si evita la trafila banca d'affari + broker: un incontro esplorativo, NDA reciproco, offerta chiara entro 30 giorni. Il processo è confidenziale e diretto, senza intermediari." },
      { q: "Quanto vale una PMI meccanica lombarda con €10M di fatturato?", a: "Il range indicativo è 3.5x-6.2x EBITDA. Con margine EBITDA del 15% (€1.5M), il valore va da €5.3M (conservativo) a €9.3M (potenziale). Il nostro motore calcola il tuo range in 10 minuti su /valutazione." },
      { q: "Chi acquista PMI in provincia di Brescia o Bergamo?", a: "GIANO acquisisce PMI in Lombardia con ricavi €5-50M ed EBITDA >€1M, in nicchie difendibili, per mantenerle nel gruppo permanentemente. Nessun exit, nessuna delocalizzazione." },
    ],
  },
  veneto: {
    name: "Veneto",
    tagline: "18% del deal flow. Vicenza, Treviso e Padova nel cuore del capitalismo di distretto.",
    ateco_top: "Meccanica, occhialeria, calzaturiero, chimica",
    provinces: ["Vicenza", "Treviso", "Padova", "Verona", "Venezia"],
    share_pct: 18.0,
    avg_multiple: 4.8,
    pmi_count: 28100,
    intro: "Il Veneto è la seconda regione italiana per intensità di deal M&A nel micro-mid market. Il modello del distretto industriale — dalla concia di Arzignano all'occhialeria del Cadore — ha creato migliaia di PMI di eccellenza, oggi guidate da imprenditori sopra i 60 anni senza successione. GIANO le acquisisce mantenendo brand, sede e squadra.",
    faq: [
      { q: "Come vendere un'azienda meccanica in Veneto?", a: "GIANO acquista aziende meccaniche venete con EBITDA >€1M in nicchie difendibili. Processo confidenziale, offerta entro 30 giorni, mai reselling futuro." },
      { q: "Quanto vale un'azienda di occhialeria del Bellunese?", a: "Range 4.2x-6.5x EBITDA per aziende con posizionamento premium e canali export. Il nostro motore stima il tuo valore reale in 10 minuti su /valutazione." },
      { q: "GIANO acquisisce nel Distretto della Concia di Arzignano?", a: "Sì: il settore chimico-conciario è una delle nicchie target GIANO, con multipli 4.5x-7.2x EBITDA per aziende con certificazioni ambientali complete." },
    ],
  },
  "emilia-romagna": {
    name: "Emilia-Romagna",
    tagline: "15% del deal flow. Motor Valley, food valley e biomedicale con i multipli più alti d'Italia.",
    ateco_top: "Automotive, agroalimentare, packaging, biomedicale",
    provinces: ["Bologna", "Modena", "Parma", "Reggio Emilia", "Rimini", "Forlì-Cesena"],
    share_pct: 15.0,
    avg_multiple: 5.3,
    pmi_count: 24800,
    intro: "L'Emilia-Romagna vanta i multipli EBITDA più alti d'Italia nel micro-mid market (media 5.3x contro 4.8x nazionale). Motor Valley da Modena a Bologna, Food Valley da Parma a Reggio Emilia, biomedicale nel Mirandolese: aziende di eccellenza globale con successione irrisolta. GIANO le tiene italiane e permanenti.",
    faq: [
      { q: "Quanto vale un'azienda alimentare emiliana con EBITDA €2M?", a: "Con multipli 4.2x-7.0x del comparto food premium, il valore va da €8.4M (conservativo) a €14M (potenziale). Il /valutazione stima il tuo range in 10 minuti." },
      { q: "Vendere azienda automotive Modena Bologna?", a: "GIANO acquisisce fornitori automotive T1-T2 con posizionamento tecnologico. Preservazione del brand e della sede sono garanzie contrattuali." },
      { q: "Chi compra aziende biomedicali nel Mirandolese?", a: "Il settore biomedicale è tra i target GIANO con multipli premium (5.0x-8.5x EBITDA). Cerchiamo aziende con brevetti o certificazioni CE MDR." },
    ],
  },
  piemonte: {
    name: "Piemonte",
    tagline: "11% del deal flow. Torino automotive, Cuneo food, Novara chimica.",
    ateco_top: "Automotive, food, chimica, aerospace",
    provinces: ["Torino", "Cuneo", "Novara", "Alessandria", "Asti"],
    share_pct: 11.0,
    avg_multiple: 4.6,
    pmi_count: 18300,
    intro: "Il Piemonte è l'unico distretto italiano con eredità automotive integrata (Torino) affiancata a food premium (Alba, Cuneo) e chimica fine (Novara). Molte fornitrici T1 di FIAT/Stellantis oggi cercano una casa permanente italiana dopo l'exit del fondatore.",
    faq: [
      { q: "Vendere azienda automotive T1 T2 Torino?", a: "GIANO acquisisce fornitori automotive piemontesi con posizionamento tecnologico e diversificazione clienti. Prezzo entro 30 giorni." },
      { q: "Quanto vale un'azienda alimentare cuneese?", a: "Le eccellenze del food langhettano (vino, cioccolato, tartufo) hanno multipli 4.2x-7.0x EBITDA. Calcola il tuo valore su /valutazione." },
      { q: "GIANO acquista anche PMI aerospace novaresi o alessandrine?", a: "Sì: aerospace e chimica fine sono nicchie target GIANO con multipli 4.5x-7.2x. Priorità ad aziende con certificazioni EN 9100/AS9100 o registrazione REACH complete." },
    ],
  },
  toscana: {
    name: "Toscana",
    tagline: "9% del deal flow. Distretti della pelletteria, del marmo, dei sanitari.",
    ateco_top: "Pelletteria, marmo, ceramica, tessile",
    provinces: ["Firenze", "Prato", "Pisa", "Lucca", "Arezzo", "Massa-Carrara"],
    share_pct: 9.0,
    avg_multiple: 4.9,
    pmi_count: 15600,
    intro: "La Toscana è l'atelier del lusso italiano: pelletteria fiorentina, marmo di Carrara, moda pratese, ceramica sanitaria di Empoli. Ogni distretto ha centinaia di PMI storiche con proprietà familiari in transizione. GIANO preserva il know-how, il brand e la sede.",
    faq: [
      { q: "Vendere azienda pelletteria fiorentina senza broker?", a: "GIANO acquista aziende del distretto pelletteria/scarpe fiorentino con capacità produttiva Made in Italy certificata. Nessun broker, nessuna delocalizzazione." },
      { q: "Quanto vale un'azienda del marmo di Carrara?", a: "Marmo e pietre naturali hanno multipli 3.4x-5.8x EBITDA. Il valore aumenta con contratti export pluriennali e certificazioni ambientali." },
      { q: "GIANO acquisisce anche nella moda pratese e nella ceramica di Empoli?", a: "Sì: tessile pratese e ceramica sanitaria/piastrelle sono nicchie target GIANO con multipli 3.0x-5.5x EBITDA. Cerchiamo aziende con brand riconoscibile e canale export attivo." },
    ],
  },
};

export default function LandingRegionale() {
  const { region } = useParams();
  const { lang } = useI18n();
  const data = REGIONS[region];
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    if (!data) return;
    // Fetch fino a 10 articoli per regione, poi ordina lato client mettendo
    // PRIMA quelli region-specific (region_tags contiene SOLO la regione corrente),
    // POI quelli generalisti che includono anche altre regioni. In questo modo
    // articoli come 'food-valley-emilia-romagna' o 'meccanica-lombarda' appaiono
    // sempre nella top-3 della loro landing regionale.
    api.get(`/insights?region_tag=${region}&category=insight&limit=10`)
      .then(r => {
        const list = r.data || [];
        const specific = list.filter(a => (a.region_tags || []).length === 1);
        const generalist = list.filter(a => (a.region_tags || []).length > 1);
        setRelatedArticles([...specific, ...generalist].slice(0, 3));
      });
  }, [region, data]);

  if (!data) return <Navigate to="/" replace />;

  const path = `/vendere-azienda/${region}`;
  const canonicalName = data.name;

  return (
    <main data-testid={`region-page-${region}`}>
      <SEO
        title={`Vendere un'azienda in ${canonicalName} — Successione PMI`}
        description={`Come vendere una PMI in ${canonicalName} senza broker: GIANO Holding acquisisce aziende ${data.ateco_top.toLowerCase()} da €5M a €50M di fatturato e le mantiene nel gruppo permanentemente. ${data.pmi_count.toLocaleString('it-IT')} PMI in transizione, multiplo EBITDA medio ${data.avg_multiple}x. Offerta chiara entro 30 giorni.`}
        path={path}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "name": `Vendere un'azienda in ${canonicalName}`,
              "url": `https://gianoholding.com${path}`,
              "inLanguage": "it-IT",
              "isPartOf": { "@id": "https://gianoholding.com/#website" },
              "about": { "@id": "https://gianoholding.com/#organization" },
            },
            {
              "@type": "FAQPage",
              "mainEntity": data.faq.map(f => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
              })),
            },
          ],
        }}
      />

      <section style={{ background: "var(--midnight)", color: "var(--bone)" }} className="py-20 md:py-28">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--gold)" }}>
            <MapPin size={12} style={{ display: "inline", marginRight: 6 }} />
            Successione PMI · {canonicalName}
          </div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 1100 }}>
            Vendere un'azienda in <span style={{ color: "var(--gold)", fontStyle: "italic" }}>{canonicalName}</span>.<br />
            Senza broker, senza exit.
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 820, opacity: 0.85, lineHeight: 1.7 }}>
            {data.tagline}
          </p>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--bone)" }}>
        <div className="container-x">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" style={{ borderTop: "1px solid var(--border)", paddingTop: 32 }}>
            <Metric icon={<Building2 size={16} />} value={data.pmi_count.toLocaleString('it-IT')} label={`PMI in transizione in ${canonicalName}`} />
            <Metric icon={<TrendingUp size={16} />} value={`${data.share_pct}%`} label="Quota del deal flow nazionale" />
            <Metric icon={<TrendingUp size={16} />} value={`${data.avg_multiple}x`} label="Multiplo EBITDA medio regionale" />
            <Metric icon={<Users size={16} />} value={data.provinces.length} label={`Province coperte: ${data.provinces.slice(0,3).join(", ")}…`} />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ background: "var(--muted)" }}>
        <div className="container-x" style={{ maxWidth: 820 }}>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Perché GIANO in {canonicalName}</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 44px)" }}>Il capitale paziente italiano per le PMI di {canonicalName}</h2>
          <p className="mt-6 text-lg" style={{ lineHeight: 1.75, opacity: 0.85 }}>{data.intro}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/valutazione" className="btn-primary">Calcola il valore della tua azienda <ArrowRight size={16} /></Link>
            <Link to="/metodo" className="btn-ghost">Come funziona il metodo GIANO</Link>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--bone)" }}>
        <div className="container-x" style={{ maxWidth: 820 }}>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Domande frequenti · {canonicalName}</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}>Quello che gli imprenditori ci chiedono</h2>
          <div className="mt-10 space-y-6">
            {data.faq.map((f, i) => (
              <div key={i} className="card-flat p-6 md:p-8" data-testid={`faq-${i}`}>
                <h3 className="serif-display" style={{ fontSize: 22 }}>{f.q}</h3>
                <p className="mt-3 text-sm" style={{ lineHeight: 1.7, opacity: 0.8 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" style={{ background: "var(--midnight)", color: "var(--bone)" }}>
        <div className="container-x text-center">
          <h2 className="serif-display" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", maxWidth: 900, margin: "0 auto" }}>
            Ti riconosci in una di queste storie?
          </h2>
          <p className="mt-6 text-lg" style={{ opacity: 0.85, maxWidth: 640, margin: "24px auto 0", lineHeight: 1.7 }}>
            Un incontro riservato, un'offerta chiara entro 30 giorni. Nessun broker, nessuna banca d'affari.
          </p>
          <Link to="/#contact" className="btn-primary mt-10 inline-flex" style={{ background: "var(--gold)", color: "var(--midnight)", borderColor: "var(--gold)" }}>
            Parla con GIANO <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="py-16 md:py-20" style={{ background: "var(--bone)" }}>
          <div className="container-x">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="overline" style={{ color: "var(--terracotta)" }}>Editoriale · Team M&amp;A GIANO</div>
                <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}>Approfondimenti per {canonicalName}</h2>
              </div>
              <Link to="/insights" className="text-sm font-mono" style={{ color: "var(--terracotta)", textDecoration: "underline", textUnderlineOffset: 4 }} data-testid="link-all-insights">
                Vedi tutti gli articoli →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10" data-testid="region-related-articles">
              {relatedArticles.map(art => (
                <ArticleCard key={art.id} insight={art} lang={lang} compact />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function Metric({ icon, value, label }) {
  return (
    <div>
      <div style={{ color: "var(--terracotta)" }}>{icon}</div>
      <div className="serif-display mt-2" style={{ fontSize: 40, lineHeight: 1 }}>{value}</div>
      <div className="mt-3 text-sm" style={{ opacity: 0.75, lineHeight: 1.5 }}>{label}</div>
    </div>
  );
}

export const REGION_SLUGS = Object.keys(REGIONS);
